import React, { useState, useEffect } from 'react';
import { HTMLTemplateModel } from '../../../models/html.template.model';
import { DocumentTemplateModel } from '../../../models/document.template.model';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { selectUser } from '../../../slices/user/userSlice';
import { selectAllDocuments } from '../../../slices/documents/documentsSlice';
import { useAppDispatch } from '../../../common/hooks/useAppDispatch';
import { NWClient } from '../../../client/NWClient';
import { toast } from 'react-toastify';
import { fetchDisclaimerInfo } from '../../../slices/disclaimer/disclaimerSlice';

export const useLibraryItemDetails = () => {
  const { id } = useParams();
  const { name: templateCategory } = useParams();
  const [params] = useSearchParams();
  const currentUser = useAppSelector(selectUser);
  const templateViewEditMode = params.get('templateViewEditMode');
  const generateDocumentParam = params.get('generateDocument');
  const { t } = useTranslation();
  const [dlHTML, setDLHTML] = useState<string>('');
  const [loadingHTML, setLoadingHTML] = useState<boolean>(true);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [contractCreationEnabled, setContractCreationEnabled] = useState<boolean>(false);
  const navigate = useNavigate();
  const documents = useAppSelector(selectAllDocuments).filter((el) => el.user === currentUser.id);
  const [templateItem, setTemplateItem] = useState<HTMLTemplateModel | DocumentTemplateModel>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  const templateTitle = templateItem
    ? templateItem.name
      ? templateItem.name.replace('.pdf', '').replace('.doc', '').replace('.docx', '')
      : 'document_upload' in templateItem &&
        templateItem.document_upload &&
        'updated' in templateItem &&
        documents.length > 0
      ? documents.find((el) => el.id === templateItem.document_upload).name +
        '_' +
        templateItem.updated
      : t('templatePage.headerDefault')
    : t('templatePage.headerDefault');

  const getTemplateHTML = () => {
    NWClient.get(
      `${templateCategory === 'company-templates' ? 'document-template' : 'document-library'}`,
      Number(id)
    )
      .then((res: HTMLTemplateModel) => {
        setTemplateItem(res);
        if (templateCategory !== 'company-templates') {
          NWClient.get('document-library-html', Number(id))
            .then((res: { id: number; html: string }) => {
              setDLHTML(res.html);
            })
            .catch((error) => {
              toast.error(error.message ? error.message : t('messages.errorOccurred'));
            })
            .finally(() => {
              setLoadingHTML(false);
            });
        } else {
          setDLHTML(res.content);
        }
      })
      .catch((error) => {
        toast.error(error.message ? error.message : t('messages.errorOccurred'), {
          theme: 'colored',
        });
      })
      .finally(() => {
        templateCategory === 'company-templates' && setLoadingHTML(false);
      });
  };

  const handleSave = () => {
    setLoading(true);
    const data = { ...templateItem };
    data.user = currentUser.id;
    data.name = templateItem.name
      ? templateItem.name.replace('.pdf', '').replace('.doc', '').replace('.docx', '')
      : templateTitle;
    templateCategory === 'company-templates'
      ? 'content' in data && (data.content = dlHTML)
      : 'html' in data && (data.html = dlHTML);
    NWClient.put(
      `${templateCategory === 'company-templates' ? 'document-template' : 'document-library'}`,
      Number(id),
      data,
      true
    )
      .then(() => {
        toast.success(t('messages.templateUpdated'));
        setEditMode(false);
      })
      .catch((error) => {
        toast.error(error.message || t('messages.errorOccurred'));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getTemplateHTML();
    dispatch(fetchDisclaimerInfo());
  }, []);

  useEffect(() => {
    if (generateDocumentParam) {
      setContractCreationEnabled(true);
    }
  }, [generateDocumentParam]);

  return {
    loading,
    dlHTML,
    setDLHTML,
    templateTitle,
    editMode,
    setEditMode,
    loadingHTML,
    setLoadingHTML,
    contractCreationEnabled,
    setContractCreationEnabled,
    templateCategory,
    templateItem,
    handleSave,
  };
};
